/**
 * SBO shared cart actions.
 *
 * Was snippets/sbo-v3-motion.liquid, which 22 sections rendered — the
 * `{% assign %}` guard there could never work, because `{% render %}` gets
 * its own scope, so every page shipped up to nine copies of this script.
 * layout/theme.liquid now loads it once.
 *
 * Provides: window.SBOCartNotice, window.SBOCartEvents, the delegated
 * [data-sbo3-add-cart] handler, the /cart/add form interceptor that keeps
 * the shopper on the page, and the shipping-threshold currency swap.
 */

(() => {
  const notifications = document.querySelectorAll('#sbo3-cart-notification');
  if (window.SBOCartNotice) {
    notifications.forEach((node, index) => { if (index > 0) node.remove(); });
    return;
  }

  const notification = notifications[0];
  if (!notification) return;

  const title = notification.querySelector('.sbo3-cart-notification__title');
  const message = notification.querySelector('.sbo3-cart-notification__message');
  const close = notification.querySelector('.sbo3-cart-notification__close');
  let timeout;

  function hide() {
    notification.hidden = true;
  }

  function show(options = {}) {
    window.clearTimeout(timeout);
    title.textContent = options.error ? 'Could not add product' : 'Added to cart';
    message.textContent = options.message || (options.error ? 'Please try again.' : 'Your product is ready for checkout.');
    notification.hidden = false;
    timeout = window.setTimeout(hide, options.error ? 5200 : 4200);
  }

  close.addEventListener('click', hide);
  window.SBOCartNotice = { show, hide };

  let cartMutationRefreshQueue = Promise.resolve();

  function cartRequestWithTimeout(url, options = {}, responseType = 'json', timeoutMs = 12000) {
    const controller = new AbortController();
    let requestTimeout;
    const timeoutPromise = new Promise((_, reject) => {
      requestTimeout = window.setTimeout(() => {
        controller.abort();
        const error = new Error('Cart request timed out');
        error.name = 'AbortError';
        reject(error);
      }, timeoutMs);
    });
    const requestPromise = fetch(url, { ...options, signal: controller.signal }).then(async response => ({
      response,
      data: await response[responseType]()
    }));

    return Promise.race([requestPromise, timeoutPromise])
      .finally(() => window.clearTimeout(requestTimeout));
  }

  async function fetchCartSnapshot(cartUrl) {
    const { response, data } = await cartRequestWithTimeout(`${cartUrl}.js`, {
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) throw new Error(`Cart refresh returned ${response.status}`);
    return data;
  }

  async function refreshAfterCartMutation(detail = {}) {
    let cartData = detail.cartData || null;
    const cartUrl = (typeof routes !== 'undefined' && routes.cart_url) || '/cart';

    if (!cartData || !Array.isArray(cartData.items) || !Number.isFinite(Number(cartData.item_count))) {
      try {
        cartData = await fetchCartSnapshot(cartUrl);
      } catch (error) {
        console.error('[SBO cart] Could not read the updated cart', error);
      }
    }

    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      try {
        await publish(PUB_SUB_EVENTS.cartUpdate, {
          source: detail.source || 'sbo-v3',
          cartData: cartData || undefined
        });
      } catch (error) {
        console.error('[SBO cart] Could not refresh cart surfaces', error);
      }
    }

    document.dispatchEvent(new CustomEvent('cart:updated', {
      bubbles: true,
      detail: { ...detail, action: detail.action || 'add', cartData }
    }));
    return cartData;
  }

  function afterCartMutation(detail = {}) {
    const queuedRefresh = cartMutationRefreshQueue.then(
      () => refreshAfterCartMutation(detail),
      () => refreshAfterCartMutation(detail)
    );
    cartMutationRefreshQueue = queuedRefresh.catch(() => undefined);
    return queuedRefresh;
  }

  window.SBOCartEvents = {
    ...(window.SBOCartEvents || {}),
    afterMutation: afterCartMutation,
    requestWithTimeout: cartRequestWithTimeout
  };

  /* ── Canonical add-to-cart ──
     One delegated handler for every [data-sbo3-add-cart] button on the page,
     so collection, search, curated, featured and recommendation cards share
     identical loading, success and failure behaviour. */
  const inFlight = new WeakSet();

  function readCardButtonLabels(button) {
    const full = button.querySelector('[data-sbo3-action-label="full"]');
    const short = button.querySelector('[data-sbo3-action-label="short"]');
    return {
      full: full ? full.textContent : button.textContent,
      short: short ? short.textContent : button.textContent
    };
  }

  function setCardButtonLabels(button, fullLabel, shortLabel = fullLabel) {
    const full = button.querySelector('[data-sbo3-action-label="full"]');
    const short = button.querySelector('[data-sbo3-action-label="short"]');
    if (!full || !short) {
      button.textContent = fullLabel;
      return;
    }
    full.textContent = fullLabel;
    short.textContent = shortLabel;
  }

  /* data-sbo3-properties maps a variant id to the line item properties it
     goes into the cart with, e.g. {"123": {"Pre-order": "Ships on arrival…"}}
     (see snippets/sbo-preorder.liquid). Unreadable JSON adds the lines plain. */
  function readLineProperties(button) {
    try {
      const parsed = JSON.parse(button.dataset.sbo3Properties || '{}');
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (error) {
      return {};
    }
  }

  async function addCardToCart(button) {
    /* data-variant-ids="1,2" adds a bundle in one request; cards pass one id. */
    const variantIds = String(button.dataset.variantIds || button.dataset.variantId || '')
      .split(',')
      .map(Number)
      .filter(Boolean);
    const lineProperties = readLineProperties(button);
    if (!variantIds.length || button.disabled || inFlight.has(button)) return;

    const original = readCardButtonLabels(button);
    const productTitle = button.dataset.productTitle || 'This product';
    inFlight.add(button);
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    setCardButtonLabels(button, 'Adding…', 'Wait…');

    try {
      const { response, data: payload } = await cartRequestWithTimeout('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: variantIds.map(id => (lineProperties[id] ? { id, quantity: 1, properties: lineProperties[id] } : { id, quantity: 1 }))
        })
      });
      if (!response.ok) {
        const error = new Error(payload.description || 'Unable to add this product.');
        error.status = response.status;
        throw error;
      }
      setCardButtonLabels(button, 'Added to cart', 'Added');
      await afterCartMutation({ source: 'sbo-v3-card', productTitle, item: payload });
      window.setTimeout(() => {
        setCardButtonLabels(button, original.full, original.short);
        button.disabled = false;
        button.removeAttribute('aria-busy');
        inFlight.delete(button);
      }, 1800);
    } catch (error) {
      if (error.name === 'AbortError') {
        const cartData = await afterCartMutation({
          source: 'sbo-v3-card-timeout',
          productTitle,
          action: 'reconcile'
        });
        const reconciled = Boolean(cartData);
        setCardButtonLabels(button, reconciled ? 'Check cart' : 'Refresh page', reconciled ? 'Cart' : 'Refresh');
        show({
          error: !reconciled,
          message: reconciled
            ? `The response was slow, so we refreshed your cart. Check ${productTitle}'s quantity before adding again.`
            : 'We could not verify your cart. Refresh the page before trying again.'
        });
        if (reconciled) {
          window.setTimeout(() => {
            setCardButtonLabels(button, original.full, original.short);
            button.disabled = false;
            button.removeAttribute('aria-busy');
            inFlight.delete(button);
          }, 2400);
        }
        return;
      }
      const message = error.name === 'AbortError'
        ? `The cart took too long to respond. ${productTitle} was not added — please try again.`
        : navigator.onLine === false
          ? `We couldn't add ${productTitle}. Check your connection and try again.`
          : error.status === 422
            ? `${productTitle} is no longer available in this option. Refresh the page or view the product details.`
            : `We couldn't add ${productTitle}. ${error.message}`;
      setCardButtonLabels(button, 'Try again', 'Retry');
      show({ error: true, message });
      window.setTimeout(() => {
        setCardButtonLabels(button, original.full, original.short);
        button.disabled = false;
        button.removeAttribute('aria-busy');
        inFlight.delete(button);
      }, 2400);
    }
  }

  document.addEventListener('click', event => {
    const button = event.target.closest('[data-sbo3-add-cart]');
    if (button) addCardToCart(button);
  });

  function updateShippingThreshold(currencyCode, countryCode) {
    const currency = String(currencyCode || '').toUpperCase();
    const country = String(countryCode || '').toUpperCase();
    const usesCanadianThreshold = country === 'CA' || (!country && currency === 'CAD');
    document.querySelectorAll('[data-sbo3-shipping-threshold]').forEach(node => {
      const cadThreshold = node.dataset.sbo3ShippingCad;
      const usdThreshold = node.dataset.sbo3ShippingUsd;
      if (!cadThreshold || !usdThreshold) return;
      node.textContent = usesCanadianThreshold
        ? cadThreshold
        : usdThreshold;
    });
  }

  const activeLocale = document.querySelector('[data-sbo3-current-currency]');
  if (activeLocale) {
    updateShippingThreshold(
      activeLocale.dataset.sbo3CurrentCurrency,
      activeLocale.dataset.sbo3CurrentCountry
    );
  }

  document.addEventListener('sbo3:locale-change', event => {
    updateShippingThreshold(
      event.detail && event.detail.currencyCode,
      event.detail && event.detail.countryCode
    );
  });

  document.addEventListener('cart:updated', event => {
    const productTitle = event.detail && event.detail.productTitle;
    show({ message: productTitle ? `${productTitle} is ready for checkout.` : 'Your product is ready for checkout.' });
  });

  /* Keep native Shopify add-to-cart forms on the current page as well. */
  document.addEventListener('submit', async event => {
    const form = event.target.closest('form');
    if (!form || !/\/cart\/add(?:\.js)?(?:[/?#]|$)/.test(form.action || '')) return;
    const submitter = event.submitter;
    if (form.matches('[data-sbo3-cart-native="false"]') || (submitter && submitter.closest('.shopify-payment-button'))) return;
    if (form.dataset.sbo3CartSubmitting === 'true') return;

    event.preventDefault();
    event.stopPropagation();
    form.dataset.sbo3CartSubmitting = 'true';
    const submitButton = submitter || form.querySelector('[type="submit"]');
    const originalLabel = submitButton && submitButton.textContent;
    if (submitButton) {
      submitButton.disabled = true;
      submitButton.setAttribute('aria-busy', 'true');
      submitButton.textContent = 'Adding…';
    }

    try {
      const { response, data: item } = await cartRequestWithTimeout('/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form)
      });
      if (!response.ok) throw new Error(item.description || 'Unable to add this product.');
      const productTitle = form.dataset.productTitle || item.product_title || '';
      if (submitButton) {
        submitButton.textContent = 'Added to cart';
        window.setTimeout(() => {
          submitButton.textContent = originalLabel;
          submitButton.disabled = false;
          submitButton.removeAttribute('aria-busy');
        }, 1800);
      }
      await afterCartMutation({ source: 'sbo-v3-form', item, productTitle });
    } catch (error) {
      if (error.name === 'AbortError') {
        const cartData = await afterCartMutation({ source: 'sbo-v3-form-timeout', action: 'reconcile' });
        const reconciled = Boolean(cartData);
        if (submitButton) {
          submitButton.textContent = reconciled ? 'Check cart' : 'Refresh page';
          if (reconciled) {
            window.setTimeout(() => {
              submitButton.textContent = originalLabel || 'Add to cart';
              submitButton.disabled = false;
              submitButton.removeAttribute('aria-busy');
            }, 2400);
          }
        }
        show({
          error: !reconciled,
          message: reconciled
            ? 'The response was slow, so we refreshed your cart. Check the quantity before adding again.'
            : 'We could not verify your cart. Refresh the page before trying again.'
        });
        return;
      }
      if (submitButton) {
        submitButton.textContent = originalLabel || 'Add to cart';
        submitButton.disabled = false;
        submitButton.removeAttribute('aria-busy');
      }
      show({ error: true, message: error.name === 'AbortError' ? 'The cart took too long to respond. Please try again.' : error.message });
    } finally {
      delete form.dataset.sbo3CartSubmitting;
    }
  }, true);
})();
